<template lang="pug">
.attr-swatch(:class="code")
  Icon.svg(v-if="svg" :name="svg")
  //- Texture only: the swatch button around this already has the option's name as its aria-label
  .bg(v-if="!svg || style")
    img.raster(v-if="raster" :src="raster" alt="")
    .color(:style="style")
    img.knurl(v-if="knurled" src="../../assets/knurled.svg" alt="")
</template>
<script setup lang="ts">
import { computed } from 'vue'
import { getCloudinaryUrl } from '../../js/helpers'
import { COLORS, HARDCODE, KNURLRASTER, RASTER, SVG } from '../../js/constants'

function toKey(key: string | undefined, code: string): string {
  let cleansedKey = (key || '').toLowerCase().replace(/(edition|smooth|none|center|high|gloss|proprietary|matte)/g, '')
  if (code !== 'rig_color') {
    // Strip the word cerakote from everything except racks, used for racks to show a cerakote logo swatch
    cleansedKey = cleansedKey.replace(/(cerakote)/g, '')
  }

  return cleansedKey.replace(/[^a-z0-9_/]/g, '').replace(/(with)/g, '/')
}

const props = withDefaults(defineProps<{
  code?: string
  value?: string
  colors?: Record<string, string>
}>(), {
  code   : '',
  value  : '',
  colors : () => COLORS,
})

const key = computed(() => toKey(props.value, props.code))

const knurled = computed(() => key.value.indexOf('knurled') !== -1)

const raster = computed(() => {
  if (HARDCODE[key.value]) {
    return getCloudinaryUrl(HARDCODE[key.value], 300, 300, '#fff', ['c_pad'])
  }

  if (props.code === 'handle_color' && KNURLRASTER[key.value]) {
    return getCloudinaryUrl(`UI/swatches/knurled/${key.value}`, 300, 300, '#fff', ['c_pad'])
  }

  if (RASTER[key.value.split('/')[0]]) {
    const swatchKey = props.code === 'rig_color' ? `colors/${key.value}` : key.value.split('/')[0]

    return getCloudinaryUrl(`/UI/swatches/${swatchKey}`, 300, 300, '#fff', ['c_pad'])
  }

  return undefined
})

const style = computed<Record<string, string | undefined> | undefined>(() => {
  if (HARDCODE[key.value] || ['rx_handle_color', 'handle_color'].includes(props.code || '')) {
    return undefined
  }

  const [one, two] = key.value.split('/')

  if (props.colors?.[one] && props.colors?.[two]) {
    return { 'background-image': `linear-gradient(-45deg, ${props.colors[two]}, ${props.colors[two]} 50%, ${props.colors[one]} calc(50% + 1px), ${props.colors[one]} 100%)` }
  }

  if (RASTER[one] && props.colors?.[two]) {
    return { 'background-image': `linear-gradient(-45deg, ${props.colors[two]}, ${props.colors[two]} 50%, transparent calc(50% + 1px), transparent 100%)` }
  }

  if (props.colors?.[one] || props.code === 'rig_color') {
    return { background: props.colors?.[one] }
  }

  return undefined
})

const svg = computed<string | undefined>(() => {
  if (SVG[props.code || ''] && !style.value && !raster.value) {
    if (SVG[props.code || ''] === 'attributes') {
      return `${SVG[props.code || '']}/${props.code}_${key.value}`
    }
    
    return `${SVG[props.code || '']}/${key.value}`
  }

  return undefined
})
</script>
<style lang="scss">
.attr-swatch {
  min-width: 50px;
  max-width: 250px;

  .bg {
    position: relative;
    width: 100%;
    padding-top: 100%;
    object-fit: contain;
  }
  .svg {
    width: auto;
    height: auto;
    max-height: 100%;
    max-width: 100%;
  }

  .knurl, .raster, .color {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }

  .knurl {
    opacity: 0.5;
  }
}
</style>
